/**
 * Architecture object schema validation with discriminated unions (SEC-002)
 * @module schemas/object
 */

import { z } from 'zod';
import {
  OBJECT_TYPES,
  OBJECT_STATUSES,
  LIFECYCLE_PHASES,
  CRITICALITY_LEVELS,
  DATA_CLASSIFICATIONS,
  TECHNICAL_DEBT_LEVELS,
  HOSTING_MODELS,
  TECHNOLOGY_CATEGORIES,
  SUPPORT_LEVELS,
  LICENSE_TYPES,

  STRATEGIC_IMPORTANCE,
  INVESTMENT_PROFILES,
  ORGANIZATION_TYPES,
} from '../constants/object-types.js';
import { userSummarySchema } from './user.schema.js';
import { tagResponseSchema } from './tag.schema.js';
import { paginationSchema, uuidSchema } from './common.schema.js';

// =============================================================================
// Enum Schemas
// =============================================================================

export const objectTypeSchema = z.enum(OBJECT_TYPES);
export const objectStatusSchema = z.enum(OBJECT_STATUSES);
export const lifecyclePhaseSchema = z.enum(LIFECYCLE_PHASES);
export const criticalityLevelSchema = z.enum(CRITICALITY_LEVELS);
export const dataClassificationSchema = z.enum(DATA_CLASSIFICATIONS);
export const technicalDebtLevelSchema = z.enum(TECHNICAL_DEBT_LEVELS);
export const hostingModelSchema = z.enum(HOSTING_MODELS);
export const technologyCategorySchema = z.enum(TECHNOLOGY_CATEGORIES);
export const supportLevelSchema = z.enum(SUPPORT_LEVELS);
export const licenseTypeSchema = z.enum(LICENSE_TYPES);
export const capabilityLevelSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
export const strategicImportanceSchema = z.enum(STRATEGIC_IMPORTANCE);
export const investmentProfileSchema = z.enum(INVESTMENT_PROFILES);
export const organizationTypeSchema = z.enum(ORGANIZATION_TYPES);

// =============================================================================
// Discriminated Union Attribute Schemas (SEC-002)
// =============================================================================

/** Application attributes schema */
export const applicationAttributesSchema = z.object({
  type: z.literal('Application'),
  version: z.string().max(50).optional(),
  vendor: z.string().max(255).optional(),
  deployedDate: z.string().date().optional(),
  retirementDate: z.string().date().optional(),
  criticality: criticalityLevelSchema,
  dataClassification: dataClassificationSchema,
  annualCost: z.number().nonnegative().optional(),
  riskScore: z.number().int().min(1).max(5).optional(),
  technicalDebt: technicalDebtLevelSchema.optional(),
  hostingModel: hostingModelSchema,
  environment: z.string().max(50).optional(),
  productionUrl: z.string().url().optional(),
  documentationUrl: z.string().url().optional(),
  repositoryUrl: z.string().url().optional(),
});

/** Technology attributes schema */
export const technologyAttributesSchema = z.object({
  type: z.literal('Technology'),
  category: technologyCategorySchema,
  currentVersion: z.string().max(50).optional(),
  latestVersion: z.string().max(50).optional(),
  endOfLifeDate: z.string().date().optional(),
  supportLevel: supportLevelSchema,
  vendor: z.string().max(255).optional(),
  licenseType: licenseTypeSchema.optional(),
  isStandard: z.boolean(),
  standardCategory: z.string().max(100).optional(),
});

/** Business capability attributes schema */
export const businessCapabilityAttributesSchema = z.object({
  type: z.literal('BusinessCapability'),
  level: capabilityLevelSchema,
  maturityScore: z.number().int().min(1).max(5).optional(),
  strategicImportance: strategicImportanceSchema,
  investmentProfile: investmentProfileSchema,
  businessDomain: z.string().max(255).optional(),
  valueStream: z.string().max(255).optional(),
});

/** Person attributes schema */
export const personAttributesSchema = z.object({
  type: z.literal('Person'),
  title: z.string().max(255).optional(),
  department: z.string().max(255).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(50).optional(),
  location: z.string().max(255).optional(),
  responsibilities: z.array(z.string().max(500)).max(20).optional(),
});

/** Organization attributes schema */
export const organizationAttributesSchema = z.object({
  type: z.literal('Organization'),
  orgType: organizationTypeSchema,
  headCount: z.number().int().nonnegative().optional(),
  costCenter: z.string().max(50).optional(),
  externalId: z.string().max(100).optional(),
});

/** Discriminated union of all attribute schemas */
export const objectAttributesSchema = z.discriminatedUnion('type', [
  applicationAttributesSchema,
  technologyAttributesSchema,
  businessCapabilityAttributesSchema,
  personAttributesSchema,
  organizationAttributesSchema,
]);

// =============================================================================
// Create Request Schemas (Discriminated by type)
// =============================================================================

const createObjectRequestBaseSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255, 'Name must not exceed 255 characters'),
  description: z.string().max(5000, 'Description must not exceed 5000 characters').optional(),
  parentId: uuidSchema.nullable().optional(),
  sortOrder: z.number().int().nonnegative().default(0),
  status: objectStatusSchema.default('draft'),
  lifecyclePhase: lifecyclePhaseSchema.optional(),
  ownerId: uuidSchema.nullable().optional(),
  tagIds: z.array(uuidSchema).optional(),
});

/** Create Application request */
export const createApplicationRequestSchema = createObjectRequestBaseSchema.extend({
  type: z.literal('Application'),
  attributes: applicationAttributesSchema.omit({ type: true }),
});

/** Create Technology request */
export const createTechnologyRequestSchema = createObjectRequestBaseSchema.extend({
  type: z.literal('Technology'),
  attributes: technologyAttributesSchema.omit({ type: true }),
});

/** Create BusinessCapability request */
export const createBusinessCapabilityRequestSchema = createObjectRequestBaseSchema.extend({
  type: z.literal('BusinessCapability'),
  attributes: businessCapabilityAttributesSchema.omit({ type: true }),
});

/** Create Person request */
export const createPersonRequestSchema = createObjectRequestBaseSchema.extend({
  type: z.literal('Person'),
  attributes: personAttributesSchema.omit({ type: true }),
});

/** Create Organization request */
export const createOrganizationRequestSchema = createObjectRequestBaseSchema.extend({
  type: z.literal('Organization'),
  attributes: organizationAttributesSchema.omit({ type: true }),
});

/** Discriminated union for create object request */
export const createObjectRequestSchema = z.discriminatedUnion('type', [
  createApplicationRequestSchema,
  createTechnologyRequestSchema,
  createBusinessCapabilityRequestSchema,
  createPersonRequestSchema,
  createOrganizationRequestSchema,
]);

// =============================================================================
// Update Request Schema
// =============================================================================

/** Update object request schema (partial, no type change allowed) */
export const updateObjectRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(5000).optional(),
  parentId: uuidSchema.nullable().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  attributes: z.record(z.string(), z.unknown()).optional(), // Partial attributes, validated by backend
  status: objectStatusSchema.optional(),
  lifecyclePhase: lifecyclePhaseSchema.optional(),
  ownerId: uuidSchema.nullable().optional(),
  tagIds: z.array(uuidSchema).optional(),
  changeReason: z.string().max(500).optional(),
});

/** Restore object request schema */
export const restoreObjectRequestSchema = z.object({
  version: z.number().int().positive().optional(),
});

// =============================================================================
// Response Schemas
// =============================================================================

/** Object summary schema */
export const objectSummarySchema = z.object({
  id: uuidSchema,
  type: objectTypeSchema,
  name: z.string(),
  status: objectStatusSchema,
  spaceId: uuidSchema,
});

/** Object response schema */
export const objectResponseSchema = z.object({
  id: uuidSchema,
  spaceId: uuidSchema,
  type: objectTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  parentId: uuidSchema.nullable(),
  sortOrder: z.number().int(),
  attributes: objectAttributesSchema,
  status: objectStatusSchema,
  lifecyclePhase: lifecyclePhaseSchema.nullable(),
  owner: userSummarySchema.nullable(),
  tags: z.array(tagResponseSchema),
  createdBy: userSummarySchema,
  updatedBy: userSummarySchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
});

/** Object list response schema */
export const objectListResponseSchema = z.object({
  data: z.array(objectResponseSchema),
  pagination: paginationSchema,
});

/** Object version response schema */
export const objectVersionResponseSchema = z.object({
  id: uuidSchema,
  objectId: uuidSchema,
  version: z.number().int().positive(),
  snapshot: objectResponseSchema,
  changedFields: z.array(z.string()),
  changedBy: userSummarySchema,
  changeReason: z.string().nullable(),
  createdAt: z.string().datetime(),
});

/** Object version list response schema */
export const objectVersionListResponseSchema = z.object({
  data: z.array(objectVersionResponseSchema),
  pagination: paginationSchema,
});

// =============================================================================
// Type Inference
// =============================================================================

export type ObjectTypeInput = z.infer<typeof objectTypeSchema>;
export type ObjectStatusInput = z.infer<typeof objectStatusSchema>;
export type LifecyclePhaseInput = z.infer<typeof lifecyclePhaseSchema>;
export type ApplicationAttributesInput = z.infer<typeof applicationAttributesSchema>;
export type TechnologyAttributesInput = z.infer<typeof technologyAttributesSchema>;
export type BusinessCapabilityAttributesInput = z.infer<typeof businessCapabilityAttributesSchema>;
export type PersonAttributesInput = z.infer<typeof personAttributesSchema>;
export type OrganizationAttributesInput = z.infer<typeof organizationAttributesSchema>;
export type ObjectAttributesInput = z.infer<typeof objectAttributesSchema>;
export type CreateObjectRequestInput = z.infer<typeof createObjectRequestSchema>;
export type UpdateObjectRequestInput = z.infer<typeof updateObjectRequestSchema>;
export type RestoreObjectRequestInput = z.infer<typeof restoreObjectRequestSchema>;
export type ObjectSummaryInput = z.infer<typeof objectSummarySchema>;
export type ObjectResponseInput = z.infer<typeof objectResponseSchema>;
export type ObjectListResponseInput = z.infer<typeof objectListResponseSchema>;
export type ObjectVersionResponseInput = z.infer<typeof objectVersionResponseSchema>;
export type ObjectVersionListResponseInput = z.infer<typeof objectVersionListResponseSchema>;

// =============================================================================
// Bulk Import Schemas (SEC-PHASE2)
// =============================================================================

/** Single item in bulk import - simplified for Excel import */
export const bulkImportItemSchema = z.object({
  type: objectTypeSchema,
  name: z.string().min(1, 'Name is required').max(255, 'Name must not exceed 255 characters'),
  description: z.string().max(5000).optional(),
  parentId: uuidSchema.nullable().optional(),
  status: objectStatusSchema.optional().default('draft'),
  lifecyclePhase: lifecyclePhaseSchema.optional(),
  ownerId: uuidSchema.nullable().optional(),
  // Flattened attributes for Excel compatibility
  attributes: z.record(z.string(), z.unknown()).optional(),
});

/** Bulk import request schema */
export const bulkImportRequestSchema = z.object({
  items: z.array(bulkImportItemSchema)
    .min(1, 'At least one item is required')
    .max(500, 'Maximum 500 items per import'),
  skipDuplicates: z.boolean().optional().default(false),
  dryRun: z.boolean().optional().default(false),
});

/** Bulk import response schema */
export const bulkImportResponseSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(z.object({
    index: z.number().int().nonnegative(),
    name: z.string(),
    error: z.string(),
  })),
  dryRun: z.boolean(),
});

export type BulkImportItemInput = z.infer<typeof bulkImportItemSchema>;
export type BulkImportRequestInput = z.infer<typeof bulkImportRequestSchema>;
export type BulkImportResponseInput = z.infer<typeof bulkImportResponseSchema>;

// =============================================================================
// Impact Analysis Schemas (SEC-PHASE2)
// =============================================================================

/** Impact analysis node */
export const impactNodeSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  type: objectTypeSchema,
  distance: z.number().int().nonnegative(),
  impactPath: z.array(z.string()),
  relationTypes: z.array(z.string()),
});

/** Impact analysis response */
export const impactAnalysisResponseSchema = z.object({
  sourceId: uuidSchema,
  sourceName: z.string(),
  sourceType: objectTypeSchema,
  direction: z.enum(['incoming', 'outgoing', 'both']),
  depth: z.number().int().positive(),
  nodes: z.array(impactNodeSchema),
  summary: z.object({
    totalAffected: z.number().int().nonnegative(),
    byType: z.record(z.string(), z.number().int().nonnegative()),
    byDistance: z.record(z.string(), z.number().int().nonnegative()),
  }),
});

export type ImpactNodeInput = z.infer<typeof impactNodeSchema>;
export type ImpactAnalysisResponseInput = z.infer<typeof impactAnalysisResponseSchema>;
